import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, LogOut, Flame, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import SEO from "../components/SEO";

const TOKEN_KEY = "gas_admin_token";
const PER_PAGE = 50;

function fmtDateTimeBR(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function capitalize(str) {
  if (!str) return "";
  return str.toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase());
}

const STATUS_LABELS = { pendente: "Pendente", respondido: "Respondido", cancelado: "Cancelado" };
const STATUS_COLORS = {
  pendente: "bg-amber-100 text-amber-700",
  respondido: "bg-green-100 text-green-700",
  cancelado: "bg-gray-200 text-gray-600",
};

function LoginForm({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post("/gas/admin/login", { password });
      if (res.data?.success) {
        sessionStorage.setItem(TOKEN_KEY, res.data.data.token);
        onLogin(res.data.data.token);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <form onSubmit={handleSubmit} className="bg-[var(--card)] border border-[var(--border)] rounded-2xl p-8 w-full max-w-sm space-y-4">
        <div className="flex items-center gap-2 justify-center mb-2">
          <Lock size={20} className="text-[var(--accent)]" />
          <h1 className="text-lg font-bold text-[var(--text)]">Pedidos de Gás</h1>
        </div>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Senha de acesso"
          autoFocus
          className="w-full px-3 py-2.5 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
        />
        {error && <p className="text-xs text-red-500">{error}</p>}
        <button
          type="submit"
          disabled={loading || !password.trim()}
          className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold text-sm px-5 py-2.5 rounded-xl transition-all"
        >
          {loading ? "Entrando..." : "Entrar"}
        </button>
      </form>
    </div>
  );
}

export default function AdminPedidosPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [pedidos, setPedidos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchPedidos = useCallback((authToken, pageNum) => {
    setLoading(true);
    axios
      .get("/gas/pedidos", { params: { page: pageNum, limit: PER_PAGE }, headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => {
        if (res.data?.success) {
          setPedidos(res.data.data);
          setTotal(res.data.total);
          setTotalPages(res.data.totalPages);
          setError(null);
        }
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken(null);
        } else {
          setError(err.response?.data?.error || err.message);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (token) fetchPedidos(token, page);
  }, [token, page, fetchPedidos]);

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const handleStatusChange = async (id, status) => {
    setUpdatingId(id);
    try {
      await axios.patch(`/gas/pedidos/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setPedidos((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  if (!token) {
    return (
      <>
        <SEO noindex title="Admin - Pedidos de Gás" />
        <LoginForm onLogin={setToken} />
      </>
    );
  }

  return (
    <div className="space-y-6">
      <SEO noindex title="Admin - Pedidos de Gás" />

      <div className="flex items-center justify-between border-b border-[var(--border)] pb-6">
        <div className="flex items-center gap-2">
          <Flame size={24} className="text-[var(--accent)]" />
          <h1 className="text-2xl font-extrabold tracking-tight text-[var(--text)]">Pedidos de Gás</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchPedidos(token, page)}
            className="p-2 rounded-lg border border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)] transition-colors"
            title="Atualizar"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)] transition-colors"
          >
            <LogOut size={14} /> Sair
          </button>
        </div>
      </div>

      <p className="text-xs text-[var(--muted)]">{total.toLocaleString("pt-BR")} pedido{total !== 1 ? "s" : ""}</p>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-500 rounded-lg p-4 text-sm">{error}</div>
      )}

      <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-left text-[10px] uppercase tracking-wider text-[var(--muted)]">
                <th className="px-4 py-3 whitespace-nowrap">Data</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Endereço de Entrega</th>
                <th className="px-4 py-3">Revenda</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {pedidos.map((p) => (
                <tr key={p.id} className="border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--border)]/10">
                  <td className="px-4 py-3 whitespace-nowrap text-[var(--muted)]">{fmtDateTimeBR(p.createdAt)}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-[var(--text)]">{p.nomeCliente}</p>
                    <p className="text-xs text-[var(--muted)] font-mono">{p.telefone}</p>
                  </td>
                  <td className="px-4 py-3 max-w-xs text-[var(--text)]">{p.enderecoEntrega}</td>
                  <td className="px-4 py-3">
                    <p className="text-[var(--text)]">{capitalize(p.razaoSocial)}</p>
                    <p className="text-xs text-[var(--muted)]">
                      {capitalize(p.municipio)}, {p.uf} · {capitalize(p.distribuidora)}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={p.status}
                      disabled={updatingId === p.id}
                      onChange={(e) => handleStatusChange(p.id, e.target.value)}
                      className={`text-xs font-semibold rounded-full px-2.5 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-[var(--accent)] ${STATUS_COLORS[p.status] || ""}`}
                    >
                      {Object.entries(STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && pedidos.length === 0 && (
          <div className="text-center py-16 text-[var(--muted)]">
            <Flame size={32} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm font-medium">Nenhum pedido ainda</p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 py-4 border-t border-[var(--border)]">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg text-xs font-semibold border border-[var(--border)] text-[var(--muted)] disabled:opacity-40"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs text-[var(--muted)] px-2">{page} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-lg text-xs font-semibold border border-[var(--border)] text-[var(--muted)] disabled:opacity-40"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
